import { useState } from 'react';
import { Check, Upload, TrendingUp, DollarSign, Handshake, ArrowRight, CheckCircle2 } from 'lucide-react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api, { getApiError } from '../lib/axios';

interface LeadForm {
  company_name: string;
  contact_name: string;
  email: string;
  website: string;
  domain_count_estimate: string;
  message: string;
  website_confirm: string; // honeypot
}

const EMPTY_FORM: LeadForm = {
  company_name: '',
  contact_name: '',
  email: '',
  website: '',
  domain_count_estimate: '',
  message: '',
  website_confirm: '',
};

export default function Partners() {
  const [form, setForm] = useState<LeadForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof LeadForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.company_name.trim()) return setError('Company name is required.');
    if (!isEmail(form.email.trim())) return setError('Enter a valid email address.');

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim() || undefined,
        email: form.email.trim().toLowerCase(),
        website: form.website.trim() || undefined,
        message: form.message.trim() || undefined,
        website_confirm: form.website_confirm || undefined,
      };
      const n = parseInt(form.domain_count_estimate, 10);
      if (!Number.isNaN(n) && n >= 0) payload.domain_count_estimate = n;

      await api.post('/api/partners/lead', payload);
      setDone(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(getApiError(err, 'Something went wrong. Please try again or email partners@linkdiry.com.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Partner with LinkDiry - Get free SEO buyer traffic"
        description="Guest post marketplaces and publishers: list your inventory on LinkDiry, get free traffic from SEO buyers, pay only on conversions."
        canonical="/partners"
      />
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 aurora pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 pt-20 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 border border-brand-100 text-brand-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
            <Handshake className="w-3.5 h-3.5" /> For marketplaces & publishers
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] mb-5">
            Send your prices to <span className="gradient-text">SEO buyers</span>,<br className="hidden sm:block" /> not the other way around.
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            LinkDiry is where SEO professionals compare guest post prices across 50+ marketplaces.
            List your inventory, get free buyer traffic, pay only when someone clicks through and buys.
          </p>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white font-semibold rounded-xl shadow-lg shadow-brand-500/25 transition-all hover:scale-[1.02]"
          >
            Apply to list your inventory <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* 3-up value props */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: TrendingUp,
              title: 'Free buyer traffic',
              body: 'Every time a buyer compares prices on LinkDiry, your inventory shows up. We link out to your site with referral attribution.',
            },
            {
              icon: DollarSign,
              title: 'Pay only on conversions',
              body: 'No upfront fees. No listing fees. Standard affiliate commission on orders sourced from LinkDiry. If we don\'t drive orders, you don\'t pay.',
            },
            {
              icon: Upload,
              title: '10-minute setup',
              body: 'Send us a CSV, connect an API, or upload via our partner portal. Our team onboards your inventory within a week.',
            },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="p-6 rounded-2xl border border-slate-200 bg-white hover:border-brand-200 hover:shadow-card-hover transition-all">
              <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-brand-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50 py-16 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-3 text-center">How it works</p>
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">Four steps from hello to live</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply', body: 'Fill out the short form below. We reply within two business days.' },
              { step: '02', title: 'Share inventory', body: 'CSV, API, or webhook — whatever works for your tech stack.' },
              { step: '03', title: 'Go live', body: 'Your listings appear across search, domain detail, and the browser extension.' },
              { step: '04', title: 'Earn', body: 'Click-through revenue lands the same day. Monthly reconciliation, transparent stats.' },
            ].map(s => (
              <div key={s.step} className="bg-white p-6 rounded-2xl border border-slate-200">
                <div className="text-xs font-mono font-bold text-brand-500 mb-3">{s.step}</div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What you get as a partner</h2>
            <ul className="space-y-3">
              {[
                'Listings shown to every buyer who searches a domain you cover',
                'Price badges injected into Ahrefs, SEMrush, Moz, and Google via our extension',
                'Real-time click analytics — see which domains attract interest',
                'Partner dashboard with monthly revenue and top-performing domains',
                'Our editorial team recommends you in buying guides when you\'re competitive',
                'Dedicated integration support for API or webhook setups',
              ].map(item => (
                <li key={item} className="flex gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-2xl bg-gradient-to-br from-brand-50 to-teal-50 border border-brand-200">
            <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-3">Who this is for</p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> Guest post marketplaces (Collaborator, WhitePress-style)</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> Publisher networks with 1,000+ sites</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> Niche/regional marketplaces (French, German, Spanish, Nordic)</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-600 flex-shrink-0 mt-0.5" /> Link-building agencies with transparent pricing</li>
            </ul>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-6 mb-3">Not a fit</p>
            <ul className="space-y-2 text-sm text-slate-500">
              <li>• PBN-only sellers</li>
              <li>• Platforms without sustainable traffic data</li>
              <li>• Individual site owners (go through your marketplace instead)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="bg-slate-50 py-16 border-t border-slate-100">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Apply to list your inventory</h2>
            <p className="text-slate-600">Takes two minutes. No commitment — we reply with terms, then you decide.</p>
          </div>

          {done ? (
            <div className="p-8 rounded-2xl bg-white border border-brand-200 text-center">
              <CheckCircle2 className="w-12 h-12 text-brand-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Got it. We'll be in touch.</h3>
              <p className="text-slate-600 mb-4">
                A partnerships contact will email you within two business days. In the meantime, feel free to reply to any email we send — we read everything.
              </p>
              <button
                onClick={() => setDone(false)}
                className="text-sm font-semibold text-brand-600 hover:text-brand-700"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-card space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Company name" required>
                  <input
                    value={form.company_name}
                    onChange={update('company_name')}
                    required
                    maxLength={255}
                    placeholder="e.g. Collaborator"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                  />
                </Field>
                <Field label="Your name">
                  <input
                    value={form.contact_name}
                    onChange={update('contact_name')}
                    maxLength={255}
                    placeholder="Jane Doe"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Work email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    required
                    maxLength={255}
                    placeholder="jane@collaborator.pro"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                  />
                </Field>
                <Field label="Marketplace URL">
                  <input
                    value={form.website}
                    onChange={update('website')}
                    maxLength={500}
                    placeholder="https://collaborator.pro"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                  />
                </Field>
              </div>

              <Field label="Approximate domain count" hint="Roughly how many sites are in your inventory?">
                <input
                  type="number"
                  min={0}
                  value={form.domain_count_estimate}
                  onChange={update('domain_count_estimate')}
                  placeholder="e.g. 40000"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                />
              </Field>

              <Field label="Tell us about your marketplace" hint="Niches, regions, anything distinctive about your supply.">
                <textarea
                  value={form.message}
                  onChange={update('message')}
                  maxLength={2000}
                  rows={4}
                  placeholder="We focus on Scandinavian publishers. About 60% of our sites are in Swedish."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all resize-none"
                />
              </Field>

              {/* Honeypot — hidden from humans, filled by bots */}
              <div className="absolute -left-[10000px] w-px h-px overflow-hidden" aria-hidden="true">
                <label>
                  Website (leave empty)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website_confirm}
                    onChange={update('website_confirm')}
                  />
                </label>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white font-semibold rounded-xl shadow-lg shadow-brand-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Submit application'}
              </button>
              <p className="text-xs text-slate-400 text-center">
                We’ll never share your details. By submitting you agree to our <a href="/privacy" className="underline hover:text-slate-600">privacy policy</a>.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({
  label, required, hint, children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {hint && <span className="block text-xs text-slate-500 mb-1.5 mt-0.5">{hint}</span>}
      <div className={hint ? '' : 'mt-1.5'}>
        {children}
      </div>
    </label>
  );
}
